import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TokenRequest, AuthToken, TokenInfo } from '../models';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router, ActivatedRoute } from '@angular/router';
import {
  auth,
  googleProvider
} from '../firebase.config';

import {
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  User
} from 'firebase/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private baseUrl = 'https://auth.publichome.page/auth';
  private loggedInUser: string | null = null;
  private jwtToken: string = '';
  private provider: string = "publichome.page"

   isLoggedIn$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, private router: Router) {

 const savedLogin = localStorage.getItem('isLoggedIn') === 'true';
  if (savedLogin) {
    this.isLoggedIn$.next(true);
  } else {
    this.isLoggedIn$.next(false);
  }

  auth.onAuthStateChanged((user) => {
    if (user) {
      this.isLoggedIn$.next(true);
      localStorage.setItem('isLoggedIn', 'true');
    } else {
      this.isLoggedIn$.next(false);
      localStorage.removeItem('isLoggedIn');
    }
  });
  }

  getProvider() {
    return this.provider;
  }

  getUserToken() {
    return this.jwtToken;
  }

  setUserToken(token: string, provider: string = "publichome.page") {
    this.provider = provider;
    this.jwtToken = token;
  }

  getLoggedInUser() {
    return this.loggedInUser;
  }

  setLoggedInUser(email: string | null) {
    this.jwtToken = '';
    this.loggedInUser = email;
  }

  /**
   * Request a JWT token from backend
   */
  requestToken(payload: TokenRequest): Observable<AuthToken> {
    return this.http.post<AuthToken>(this.baseUrl + '/token', payload);
  }

  /**
   * Validate token via POST /tokeninfo
   */
  validateToken(token: string): Observable<TokenInfo> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.post<TokenInfo>(
      this.baseUrl + '/tokeninfo',
      {},
      { headers }
    );
  }

  /**
   * Request a whatsapp otp from backend
   */
  sendOtp(phone: string, medium: string = 'whatsapp') {
    return this.http.post<any>(`${this.baseUrl}/sendotp`, { phone: phone, medium: medium });
  }

  /**
   * Verify the whatsapp otp from backend
   */
  verifyOtp(phone: string, otp: string, medium: string = 'whatsapp') {
    return this.http.post<any>(`${this.baseUrl}/verifyotp`, { phone: phone, otp: otp, medium: medium });
  }

  async loginWithGoogle(returnUrl: string): Promise<User> {
    const result = await signInWithPopup(auth, googleProvider);
    if ((result as any)._tokenResponse?.isNewUser) {
      alert(`Welcome ${result.user.displayName}! Your account is ready.`);
    }

    const idToken = await result.user.getIdToken();
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/auth/login`, {}, {
          headers: { Authorization: `Bearer ${idToken}` },
          responseType: 'text'
        })
      );
      this.loggedInUser = result.user.email;
      // Redirect to the original page
      localStorage.setItem('isLoggedIn', 'true');
      this.isLoggedIn$.next(true);
      this.router.navigateByUrl(returnUrl);
    } catch (error) {
      console.error('Failed to set backend session', error);
      // We might want to logout from Firebase if backend session fails?
      // For now, proceed.
    }
    return result.user;
  }

  async sendMagicLink(email: string): Promise<void> {
    const actionCodeSettings = {
      //url: "https://publichomepage-5e16d.web.app/finish-login"
      url: `${environment.host}/finish-login`, // same domain as Hosting
      handleCodeInApp: true
    };

    await sendSignInLinkToEmail(auth, email, actionCodeSettings);

    // store email to auto-complete the final step
    localStorage.setItem('emailForSignIn', email);
  }

  async completeMagicLinkLogin(): Promise<User | null> {
    const url = window.location.href;
    if (!isSignInWithEmailLink(auth, url)) return null;

    let email = localStorage.getItem('emailForSignIn');
    if (!email) {
      // fallback if user opened link on another device
      email = window.prompt('Please enter your email');
    }

    const result = await signInWithEmailLink(auth, email!, url);
    localStorage.removeItem('emailForSignIn');

    return result.user;
  }

  logout() {
    this.jwtToken = '';
    return auth.signOut();
  }
}
